import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { glass } from "./portal";
import { useProject } from "@/lib/portal/useProject";
import {
  Ticket, Upload, ExternalLink, MessageSquare, Activity, Gauge,
  ShieldCheck, Search, Accessibility, CheckCircle2, Clock, CalendarDays,
  TrendingUp, Sparkles,
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export const Route = createFileRoute("/_authenticated/portal/")({
  component: DashboardHome,
});

function DashboardHome() {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ["portal", "me"], queryFn: async () => (await supabase.auth.getUser()).data.user });
  const { data: project } = useProject();
  const { data: health } = useQuery({
    queryKey: ["portal", "health"],
    queryFn: async () => (await supabase.from("health_scores").select("*").limit(1).maybeSingle()).data,
    enabled: !!project,
  });
  const { data: steps } = useQuery({
    queryKey: ["portal", "steps"],
    queryFn: async () => (await supabase.from("project_timeline_steps").select("*").order("step_order")).data ?? [],
    enabled: !!project,
  });
  const { data: checklist } = useQuery({
    queryKey: ["portal", "checklist"],
    queryFn: async () => (await supabase.from("onboarding_checklist").select("*").order("sort_order")).data ?? [],
    enabled: !!project,
  });
  const { data: analytics } = useQuery({
    queryKey: ["portal", "analytics"],
    queryFn: async () => (await supabase.from("analytics_snapshots").select("visitors, page_views, snapshot_date").order("snapshot_date", { ascending: false }).limit(7)).data ?? [],
    enabled: !!project,
  });

  const displayName = (me?.user_metadata as { full_name?: string } | undefined)?.full_name?.split(" ")[0] ?? "Cliente";
  const doneCount = (checklist ?? []).filter((c) => c.completed).length;
  const totalCount = (checklist ?? []).length;
  const weekVisitors = (analytics ?? []).reduce((s, a) => s + (a.visitors ?? 0), 0);
  const overallHealth = health ? Math.round(((health.speed ?? 0) + (health.seo ?? 0) + (health.security ?? 0) + (health.accessibility ?? 0)) / 4) : null;

  const toggleChecklist = async (id: string, done: boolean) => {
    await supabase.from("onboarding_checklist").update({ completed: !done, completed_at: !done ? new Date().toISOString() : null }).eq("id", id);
    qc.invalidateQueries({ queryKey: ["portal", "checklist"] });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Welcome */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Bentornato {displayName} 👋</h1>
        <p className="text-white/60 mt-1">Il tuo sito è attualmente in <span className="text-fuchsia-300">{project?.status?.replace("_", " ") ?? "produzione"}</span>.</p>
      </motion.div>

      {/* Progress + Quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className={`${glass} p-6 lg:col-span-2 relative overflow-hidden`}>
          <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-fuchsia-500/20 blur-3xl" />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/40">Website Progress</div>
              <div className="mt-2 flex items-baseline gap-3">
                <span className="text-6xl font-semibold tabular-nums bg-gradient-to-br from-white to-white/60 bg-clip-text text-transparent">{project?.progress ?? 0}%</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <div><div className="text-white/40 text-xs">Current step</div><div className="font-medium">{project?.current_step ?? "—"}</div></div>
                <div><div className="text-white/40 text-xs">Estimated delivery</div><div className="font-medium">{project?.delivery_date ? format(new Date(project.delivery_date), "d MMM 'ore' HH:mm", { locale: it }) : "—"}</div></div>
                <div className="col-span-2"><div className="text-white/40 text-xs">Project Manager</div><div className="font-medium">{project?.project_manager ?? "—"}</div></div>
              </div>
            </div>
            {overallHealth !== null && (
              <div className="text-center rounded-2xl bg-white/5 border border-white/10 p-4 min-w-[140px]">
                <div className="text-xs text-white/40">Health score</div>
                <div className="text-4xl font-semibold bg-gradient-to-br from-emerald-300 to-cyan-300 bg-clip-text text-transparent">{overallHealth}</div>
                <div className="text-[10px] text-white/40">/100</div>
              </div>
            )}
          </div>
          <div className="relative mt-6 h-3 rounded-full bg-white/5 overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${project?.progress ?? 0}%` }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="h-full bg-gradient-to-r from-fuchsia-500 via-pink-500 to-cyan-400" />
          </div>
        </motion.div>

        <div className={`${glass} p-6`}>
          <div className="text-xs uppercase tracking-widest text-white/40">Azioni rapide</div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {[
              { icon: Ticket, label: "Apri ticket", to: "/portal/tickets" },
              { icon: Upload, label: "Carica file", to: "/portal/files" },
              { icon: ExternalLink, label: "Preview sito", to: "/portal/project" },
              { icon: MessageSquare, label: "Chat", to: "/portal/messages" },
            ].map((a) => (
              <Link key={a.label} to={a.to} className="group rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] p-4 flex flex-col items-start gap-2 transition">
                <a.icon className="h-5 w-5 text-white/70 group-hover:text-white" />
                <span className="text-sm font-medium">{a.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline + Checklist */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`${glass} p-6 lg:col-span-2`}>
          <div className="flex items-center gap-2 mb-5"><Activity className="h-4 w-4 text-fuchsia-300" /><h2 className="font-semibold">Project timeline</h2></div>
          <ol className="relative border-l border-white/10 ml-3 space-y-5">
            {(steps ?? []).map((s) => (
              <li key={s.id} className="pl-6 relative">
                <span className={`absolute -left-[9px] top-1 h-4 w-4 rounded-full border-2 ${
                  s.status === "completed" ? "bg-emerald-400 border-emerald-400"
                    : s.status === "in_progress" ? "bg-fuchsia-500 border-fuchsia-500 animate-pulse"
                    : "bg-white/10 border-white/20"}`} />
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-white/50 flex items-center gap-2">
                    {s.step_date && <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{format(new Date(s.step_date), "d MMM", { locale: it })}</span>}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${
                      s.status === "completed" ? "bg-emerald-400/10 text-emerald-300"
                        : s.status === "in_progress" ? "bg-fuchsia-500/10 text-fuchsia-300"
                        : "bg-white/5 text-white/50"}`}>{s.status.replace("_", " ")}</span>
                  </div>
                </div>
                {s.notes && <p className="text-sm text-white/60 mt-1">{s.notes}</p>}
                <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-fuchsia-500 to-cyan-400" style={{ width: `${s.progress}%` }} /></div>
              </li>
            ))}
          </ol>
        </div>

        <div className={`${glass} p-6`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-300" /><h2 className="font-semibold">Onboarding smart</h2></div>
            <span className="text-xs text-white/50">{doneCount}/{totalCount}</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/5 mb-4 overflow-hidden"><div className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400" style={{ width: `${totalCount ? (doneCount / totalCount) * 100 : 0}%` }} /></div>
          <ul className="space-y-2">
            {(checklist ?? []).map((c) => (
              <li key={c.id}>
                <button onClick={() => toggleChecklist(c.id, c.completed)} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 text-left">
                  <span className={`h-5 w-5 rounded-md border-2 grid place-items-center transition ${c.completed ? "bg-emerald-400 border-emerald-400" : "border-white/20"}`}>
                    {c.completed && <CheckCircle2 className="h-3 w-3 text-black" />}
                  </span>
                  <span className={`text-sm ${c.completed ? "text-white/40 line-through" : "text-white"}`}>{c.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label="Visitatori 7gg" value={weekVisitors.toLocaleString("it-CH")} color="from-cyan-400 to-blue-500" />
        <StatCard icon={Gauge} label="Speed score" value={health?.speed ?? "—"} color="from-emerald-400 to-cyan-400" />
        <StatCard icon={Search} label="SEO score" value={health?.seo ?? "—"} color="from-fuchsia-400 to-pink-500" />
        <StatCard icon={ShieldCheck} label="Security" value={health?.security ?? "—"} color="from-amber-300 to-orange-500" />
      </div>

      <div className={`${glass} p-6 flex items-center gap-4 flex-wrap justify-between`}>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-cyan-400 grid place-items-center"><Sparkles className="h-5 w-5 text-black" /></div>
          <div>
            <div className="font-medium">Serve una modifica al sito?</div>
            <div className="text-sm text-white/60">Chiedi all'AI Assistant o apri un ticket in 30 secondi.</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/portal/assistant" className="px-4 py-2 rounded-full bg-white text-black text-sm font-medium hover:bg-white/90 flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> AI Assistant</Link>
          <Link to="/portal/tickets" className="px-4 py-2 rounded-full bg-white/10 text-white text-sm font-medium hover:bg-white/15 flex items-center gap-1.5"><Ticket className="h-4 w-4" /> Apri ticket</Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }: { icon: typeof Gauge; label: string; value: number | string; color: string }) {
  return (
    <div className={`${glass} p-4 relative overflow-hidden`}>
      <div className={`absolute -top-8 -right-8 h-24 w-24 rounded-full bg-gradient-to-br ${color} opacity-20 blur-2xl`} />
      <div className="flex items-center justify-between relative">
        <Icon className="h-4 w-4 text-white/50" />
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-white/50 mt-0.5">{label}</div>
    </div>
  );
}

export { Clock }; // avoid unused-import warnings if some icons unused elsewhere
